import { Component } from '@theme/component';
import { trapFocus, removeTrapFocus } from '@theme/focus';
import { yieldToMainThread } from '@theme/utilities';

/**
 * @typedef {{ trigger: HTMLButtonElement, dialog: HTMLDialogElement, panel: HTMLElement }} Refs
 * @extends {Component<Refs>}
 */
export class ADHeaderDrawerComponent extends Component {
    /** @type {string[]} */
    requiredRefs = ['trigger', 'dialog', 'panel'];

    #breakpoint = 'tablet';
    #abortController = new AbortController();
    /** @type {number[]} */
    #timeoutIds = [];
    #savedScrollY = 0;
    #isClosing = false;

    connectedCallback() {
        super.connectedCallback();

        if (this.dataset.breakpoint) {
            this.#breakpoint = this.dataset.breakpoint;
        }

        this.#addAccessibilityAttributes(this.querySelectorAll('summary'));
        this.#bindEvents();
    }

    disconnectedCallback() {
        super.disconnectedCallback();
        this.#abortController.abort();
        this.#timeoutIds.forEach((id) => clearTimeout(id));
        this.#timeoutIds = [];

        if (this.refs.dialog?.open) {
            this.#setPageInert(false);
            this.#unlockScroll();
            removeTrapFocus();
            this.#isClosing = false;
        }
    }

    updatedCallback() {
        this.#abortController.abort();
        this.#abortController = new AbortController();
        this.#addAccessibilityAttributes(this.querySelectorAll('summary'));
        this.#bindEvents();
    }

    toggle = () => {
        if (this.refs.dialog.open) {
            this.close();
        } else {
            this.open();
        }
    };

    open = async () => {
        const { dialog, trigger, panel } = this.refs;

        if (dialog.open) return;

        dialog.show();
        this.#timeoutIds.push(
            setTimeout(() => {
                dialog.classList.add('menu-opening');
            })
        );
        trigger.setAttribute('aria-expanded', 'true');
        this.#savedScrollY = window.scrollY;

        // Inert on <main> restyles the whole page; let the drawer paint first.
        await yieldToMainThread();
        if (!dialog.open || this.#isClosing) return;

        document.body.style.top = `-${this.#savedScrollY}px`;
        document.body.classList.add(`overflow-hidden-${this.#breakpoint}`);
        this.#setPageInert(true);
        trapFocus(panel);
    };

    /**
     * @param {Event} [event]
     */
    close = (event) => {
        if (event instanceof Event) {
            event.preventDefault();
            event.stopPropagation();
        }

        const { dialog, trigger } = this.refs;

        if (!dialog.open || this.#isClosing) return;

        this.#isClosing = true;
        dialog.classList.remove('menu-opening');
        dialog.querySelectorAll('details').forEach((details) => {
            details.removeAttribute('open');
            details.classList.remove('menu-opening');
            details.querySelector('summary')?.setAttribute('aria-expanded', 'false');
        });
        trigger.setAttribute('aria-expanded', 'false');
        this.#unlockScroll();
        this.#setPageInert(false);
        removeTrapFocus();
        trigger.focus();
        this.#closeAnimation(dialog);
    };

    /**
     * @param {Event} [event]
     */
    back = (event) => {
        if (!(event instanceof Event)) return;

        event.preventDefault();
        event.stopPropagation();

        // Declarative handlers are delegated from document, so currentTarget is never the button.
        const { target } = event;
        if (!(target instanceof Element)) return;

        const detailsElement = target.closest('details');
        if (detailsElement instanceof HTMLDetailsElement) {
            this.#closeSubmenu(detailsElement);
        }
    };

    #bindEvents() {
        const { signal } = this.#abortController;

        this.querySelectorAll('summary').forEach((summary) =>
            summary.addEventListener('click', this.#onSummaryClick, { signal })
        );

        document.addEventListener('keydown', this.#onEscape, { signal });
        document.addEventListener('keydown', this.#onKeyboardInput, { signal, capture: true });
        this.addEventListener('pointerdown', this.#onPointerInput, { signal, capture: true });

        const searchModal = document.getElementById('search-modal');
        if (searchModal) {
            searchModal.addEventListener('dialog:open', this.#onSearchOpen, { signal });
            searchModal.addEventListener('dialog:close', this.#onSearchClose, { signal });
        }
    }

    /**
     * @param {NodeListOf<HTMLElement>} summaryElements
     */
    #addAccessibilityAttributes(summaryElements) {
        summaryElements.forEach((element) => {
            element.setAttribute('role', 'button');
            if (!element.getAttribute('aria-expanded')) {
                element.setAttribute('aria-expanded', 'false');
            }
            const nextEl = element.nextElementSibling;
            if (nextEl && nextEl.id) {
                element.setAttribute('aria-controls', nextEl.id);
            }
        });
    }

    #onPointerInput = () => {
        this.dataset.inputModality = 'pointer';
    };

    #onKeyboardInput = () => {
        this.dataset.inputModality = 'keyboard';
    };

    /**
     * @param {KeyboardEvent} event
     */
    #onEscape = (event) => {
        if (event.key !== 'Escape') return;
        if (!this.refs.dialog.open) return;

        const searchDialog = document.querySelector('#search-modal dialog');
        if (searchDialog instanceof HTMLDialogElement && searchDialog.open) return;

        event.preventDefault();
        event.stopPropagation();

        const openDetailsElement = event.target instanceof Element ? event.target.closest('details[open]') : null;

        if (openDetailsElement instanceof HTMLDetailsElement && this.refs.dialog.contains(openDetailsElement)) {
            this.#closeSubmenu(openDetailsElement);
            return;
        }

        this.close();
    };

    /**
     * @param {MouseEvent} event
     */
    #onSummaryClick = (event) => {
        const summaryElement = event.currentTarget;
        if (!(summaryElement instanceof HTMLElement)) return;

        const detailsElement = summaryElement.parentElement;
        if (!(detailsElement instanceof HTMLDetailsElement)) return;

        summaryElement.setAttribute('aria-expanded', 'true');
        this.#timeoutIds.push(
            setTimeout(() => {
                detailsElement.classList.add('menu-opening');
            })
        );

        const submenu = summaryElement.nextElementSibling;
        if (submenu instanceof HTMLElement) {
            trapFocus(submenu);
        }
    };

    /**
     * @param {HTMLDetailsElement} detailsElement
     */
    #closeSubmenu(detailsElement) {
        detailsElement.classList.remove('menu-opening');
        detailsElement.querySelector('summary')?.setAttribute('aria-expanded', 'false');
        removeTrapFocus();
        this.#closeAnimation(detailsElement);
    }

    /**
     * @param {HTMLDialogElement | HTMLDetailsElement} element
     */
    #closeAnimation(element) {
        /** @type {number | undefined} */
        let animationStart;

        /**
         * @param {number} time
         */
        const handleAnimation = (time) => {
            if (animationStart === undefined) {
                animationStart = time;
            }

            const elapsedTime = time - animationStart;

            if (elapsedTime < 400) {
                window.requestAnimationFrame(handleAnimation);
                return;
            }

            if (element instanceof HTMLDialogElement) {
                element.close();
                this.#isClosing = false;
                return;
            }

            element.removeAttribute('open');
            if (this.refs.dialog.open) {
                trapFocus(this.refs.panel);
            }
        };

        window.requestAnimationFrame(handleAnimation);
    }

    #onSearchOpen = () => {
        removeTrapFocus();
    };

    #onSearchClose = () => {
        if (!this.refs.dialog.open) return;

        const openSubmenu = this.refs.dialog.querySelector('details.menu-opening > .menu-drawer__submenu');
        trapFocus(openSubmenu instanceof HTMLElement ? openSubmenu : this.refs.panel);
    };

    /**
     * @param {boolean} inert
     */
    #setPageInert(inert) {
        const main = document.getElementById('MainContent');
        const footer = document.querySelector('footer');

        if (main instanceof HTMLElement) {
            main.inert = inert;
        }
        if (footer instanceof HTMLElement) {
            footer.inert = inert;
        }
    }

    #unlockScroll() {
        document.body.classList.remove(`overflow-hidden-${this.#breakpoint}`);
        document.body.style.top = '';
        window.scrollTo(0, this.#savedScrollY);
    }
}

if (!customElements.get('na-header-drawer-component')) {
    customElements.define('na-header-drawer-component', ADHeaderDrawerComponent);
}
